/**
 * Client for the timestamped bookmarks endpoints.
 *
 * A file of its own rather than an addition to api.ts: this repository has a
 * second agent writing the backend for this same feature in parallel, and
 * api.ts is shared ground neither of us touches until the owner wires the two
 * together. The contract below is fixed in advance so this file can be
 * written against it without waiting.
 *
 * Same conventions as api.ts throughout: no explicit `credentials` (the
 * browser already sends the session cookie on a same origin request), a 401
 * announced once through the shared UNAUTHORIZED event rather than handled at
 * each of the four call sites, and errors thrown as the same ApiError class so
 * a caller that already knows that shape from api.ts recognises it here too.
 */

import { ApiError, UNAUTHORIZED } from "./api";

export interface Bookmark {
  id: number;
  book_id: string;
  time: number;
  note: string;
  created: string;
}

function raise(status: number, path: string, detail: string): never {
  if (status === 401) window.dispatchEvent(new Event(UNAUTHORIZED));
  throw new ApiError(status, detail || `${path} answered ${status}`);
}

function bookmarksUrl(bookId: string, id?: number): string {
  const base = `/api/books/${encodeURIComponent(bookId)}/bookmarks`;
  return id === undefined ? base : `${base}/${id}`;
}

async function get<T>(path: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(path, { signal });
  if (!response.ok) {
    raise(response.status, path, await response.text().catch(() => ""));
  }
  return (await response.json()) as T;
}

async function send<T>(method: "POST" | "PATCH", path: string, body: unknown): Promise<T> {
  const response = await fetch(path, {
    method,
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    let detail = "";
    try {
      detail = ((await response.json()) as { detail?: string }).detail ?? "";
    } catch {
      detail = "";
    }
    raise(response.status, path, detail);
  }
  return (await response.json()) as T;
}

export async function listBookmarks(bookId: string, signal?: AbortSignal): Promise<Bookmark[]> {
  const answer = await get<{ bookmarks: Bookmark[] }>(bookmarksUrl(bookId), signal);
  return answer.bookmarks;
}

export function addBookmark(bookId: string, time: number, note = ""): Promise<Bookmark> {
  return send<Bookmark>("POST", bookmarksUrl(bookId), { time, note });
}

export function updateBookmark(bookId: string, id: number, note: string): Promise<Bookmark> {
  return send<Bookmark>("PATCH", bookmarksUrl(bookId, id), { note });
}

export async function deleteBookmark(bookId: string, id: number): Promise<void> {
  const path = bookmarksUrl(bookId, id);
  const response = await fetch(path, { method: "DELETE" });
  if (!response.ok) {
    raise(response.status, path, await response.text().catch(() => ""));
  }
}
